import React, { useMemo, useState } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Printer, Save, X, CheckCircle, FileText, User, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../../services/api';
import { StepCompletionProps, WorkOrderDto, CreateWorkOrderDto, WorkOrderType } from '../../types';

const StepCompletion: React.FC<StepCompletionProps> = ({ 
  data, 
  onChange, 
  onNext, 
  onPrevious,
  isFirstStep, 
  isLastStep, 
  isSubmitting,
  onWorkOrderCreated // New prop to notify parent when work order is created
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [createdWorkOrder, setCreatedWorkOrder] = useState<WorkOrderDto | null>(null);

  // Preview: fetch default first page (sample) and selected template (sample) then combine
  const { data: firstPagePreviewResponse } = useQuery({
    queryKey: ['default-first-page-preview'],
    queryFn: () => apiService.previewDefaultFirstPage(),
  });

  const { data: templatePreviewResponse } = useQuery({
    queryKey: ['wizard-template-preview', data.templateId],
    queryFn: () => data.templateId ? apiService.previewWorkOrderTemplate(data.templateId!) : Promise.resolve({ success: true, data: { renderedHtml: '' } as any }),
    enabled: !!data.templateId,
  });

  const combinedPreviewHtml = useMemo(() => {
    const firstPageHtml = firstPagePreviewResponse?.data || '';
    const mainHtml = (templatePreviewResponse as any)?.data?.renderedHtml || '';
    if (!firstPageHtml && !mainHtml) return '';
    const parts = [] as string[];
    if (firstPageHtml) parts.push(firstPageHtml, '<div class="first-page-break"></div>');
    if (mainHtml) parts.push(mainHtml);
    return parts.join('\n');
  }, [firstPagePreviewResponse, templatePreviewResponse]);

  // Fetch selected vendor details to display real names using batch endpoint
  const { data: vendorsResponse } = useQuery({
    queryKey: ['completion-vendors', data.selectedVendors?.map(v => v.vendorId)],
    queryFn: async () => {
      if (!data.selectedVendors || data.selectedVendors.length === 0) return [] as any[];
      
      try {
        // Use batch endpoint to fetch all vendors in a single request
        const vendorIds = data.selectedVendors.map(v => v.vendorId);
        const batchResponse = await apiService.getVendorsBatch(vendorIds);
        
        if (batchResponse.success && batchResponse.data) {
          // Convert the record to an array
          return Object.values(batchResponse.data);
        }
        return [] as any[];
      } catch (error) {
        console.error('Failed to fetch vendors batch:', error);
        return [] as any[];
      }
    },
    enabled: !!(data.selectedVendors && data.selectedVendors.length > 0),
  });

  // Fetch assigned user details to display real name
  const { data: assignedUserResponse } = useQuery({
    queryKey: ['completion-assigned-user', data.assignedUserId],
    queryFn: async () => {
      if (!data.assignedUserId) return null;
      const response = await apiService.getUser(data.assignedUserId);
      return response.data;
    },
    enabled: !!data.assignedUserId,
  });

  // Fetch categories to convert IDs to names
  const { data: categoriesResponse } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(1, 100)
  });

  // Create work order mutation
  const createWorkOrderMutation = useMutation({
    mutationFn: async (workOrderData: CreateWorkOrderDto) => {
      return await apiService.createWorkOrderFromWizard(workOrderData);
    },
    onSuccess: (response) => {
      if (response.success && response.data) {
        setCreatedWorkOrder(response.data);
        const workOrderLabel = response.data.workOrderNumberFormatted || response.data.workOrderNumber;
        toast.success(`Work Order ${workOrderLabel} created successfully!`);

        queryClient.invalidateQueries({ queryKey: ['workorders-v2'] });
        queryClient.invalidateQueries({ queryKey: ['workOrdersV2'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-workorders'] });
        queryClient.invalidateQueries({ queryKey: ['workorders'] });

        if (onWorkOrderCreated) {
          onWorkOrderCreated();
        }

        if (data.fromEmailImport) {
          navigate(`/workorders/${response.data.id}/contact`, { replace: true });
        }
      } else {
        toast.error('Failed to create work order');
      }
    },
    onError: (error: any) => {
      console.error('=== ERROR CREATING WORK ORDER ===');
      console.error('Full error object:', error);
      console.error('Error type:', typeof error);
      console.error('Error response:', error?.response);
      console.error('Error response status:', error?.response?.status);
      console.error('Error response statusText:', error?.response?.statusText);
      console.error('Error response data:', JSON.stringify(error?.response?.data, null, 2));
      console.error('Error response data type:', typeof error?.response?.data);
      console.error('Error message:', error?.message);
      console.error('Request config:', error?.config);
      console.error('Request URL:', error?.config?.url);
      console.error('Request method:', error?.config?.method);
      console.error('Request data:', JSON.stringify(error?.config?.data, null, 2));
      
      // Extract error message from response
      let errorMessage = 'Failed to create work order. Please try again.';
      
      // Handle axios error response structure
      const responseData = error?.response?.data;
      
      if (responseData) {
        console.error('Processing responseData:', responseData);
        console.error('responseData.errors:', responseData.errors);
        console.error('responseData.Errors:', responseData.Errors);
        console.error('responseData.errors type:', typeof responseData.errors);
        console.error('responseData.errors isArray:', Array.isArray(responseData.errors));
        console.error('responseData.message:', responseData.message);
        console.error('responseData.Message:', responseData.Message);
        
        // Backend returns ResponseDto with success, message, and errors properties
        // Handle both camelCase (from JSON serializer) and PascalCase
        const errors = responseData.errors || responseData.Errors;
        const message = responseData.message || responseData.Message;
        const success = responseData.success !== undefined ? responseData.success : responseData.Success;
        
        // Check for validation errors array first (most specific)
        if (errors && Array.isArray(errors) && errors.length > 0) {
          console.error('Found errors array with', errors.length, 'items');
          const errorList = errors
            .filter((e: any) => {
              // Handle both string errors and object errors
              if (typeof e === 'string') return e.trim();
              if (typeof e === 'object' && e !== null) {
                // Check for nested error structures (e.g., { "ClientId": ["error1", "error2"] })
                return Object.values(e).some((v: any) => 
                  Array.isArray(v) ? v.some((msg: any) => typeof msg === 'string' && msg.trim()) 
                  : typeof v === 'string' && v.trim()
                );
              }
              return false;
            })
            .map((e: any) => {
              // If it's an object, extract the error messages
              if (typeof e === 'object' && e !== null) {
                const messages: string[] = [];
                Object.entries(e).forEach(([key, value]) => {
                  if (Array.isArray(value)) {
                    messages.push(...value.filter((v: any) => typeof v === 'string' && v.trim()));
                  } else if (typeof value === 'string' && value.trim()) {
                    messages.push(value);
                  }
                });
                return messages.length > 0 ? messages.join(', ') : null;
              }
              return typeof e === 'string' ? e.trim() : String(e);
            })
            .filter((e: any) => e && e.trim())
            .join('; ');
          
          console.error('Extracted error list:', errorList);
          errorMessage = errorList || 'Validation failed. Please check all required fields.';
        } 
        // Check for message property (validation failed, etc.)
        else if (message && typeof message === 'string' && message.trim()) {
          console.error('Using message property:', message);
          // If message is just "Validation failed" but we have no errors array, provide more context
          if (message.toLowerCase().includes('validation') && (!errors || errors.length === 0)) {
            errorMessage = 'Validation failed. Please check all required fields.';
          } else {
            errorMessage = message;
          }
        }
        // Handle case where responseData itself is a string
        else if (typeof responseData === 'string' && responseData.trim()) {
          console.error('ResponseData is a string:', responseData);
          errorMessage = responseData;
        }
        // Try to extract any other error information
        else {
          const errorKeys = Object.keys(responseData);
          console.error('Response data keys:', errorKeys);
          console.error('Response data values:', Object.values(responseData));
          if (errorKeys.length > 0) {
            // Try to find any string values that might be error messages
            const possibleMessages = Object.values(responseData)
              .filter((v: any) => typeof v === 'string' && v.trim())
              .filter((v: any) => !v.includes('{') && !v.includes('[')); // Exclude JSON strings
            console.error('Possible messages found:', possibleMessages);
            if (possibleMessages.length > 0) {
              errorMessage = possibleMessages[0] as string;
            } else {
              errorMessage = 'Validation failed. Please check all required fields.';
            }
          }
        }
      } 
      // Handle network errors or other axios errors
      else if (error?.message) {
        console.error('Using error.message:', error.message);
        errorMessage = error.message;
      }
      
      console.error('=== FINAL ERROR MESSAGE ===');
      console.error(errorMessage);
      toast.error(errorMessage);
    },
    onSettled: () => {
      setIsCreating(false);
    }
  });

  const handleCreateWorkOrder = async () => {
    // Prevent double-clicks and concurrent submissions
    if (isCreating || isSubmitting) {
      return;
    }

    if (!data.selectedClient || !data.selectedClientLocationId || !data.priorityId || !data.statusId || data.statusId <= 0 || !data.dueDate) {
      toast.error('Missing required information. Please go back and complete all steps.');
      return;
    }

    setIsCreating(true);

    console.log('Wizard data:', data);
    console.log('Template ID from data:', data.templateId);

    // Ensure dueDate is in ISO 8601 format (required for production)
    let formattedDueDate: string | undefined;
    if (data.dueDate) {
      try {
        // Parse the date and format as ISO 8601 with timezone
        const date = new Date(data.dueDate);
        if (isNaN(date.getTime())) {
          // If parsing fails, try to convert YYYY-MM-DD format
          if (data.dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
            formattedDueDate = `${data.dueDate}T00:00:00.000Z`;
          } else {
            formattedDueDate = data.dueDate;
          }
        } else {
          // Format as ISO 8601 string
          formattedDueDate = date.toISOString();
        }
      } catch (e) {
        console.warn('Error formatting dueDate:', e);
        // Fallback: if it's already a full ISO string, use it; otherwise convert from YYYY-MM-DD
        formattedDueDate = data.dueDate.includes('T') 
          ? data.dueDate 
          : `${data.dueDate}T00:00:00.000Z`;
      }
    }

    // Build work order data, ensuring proper formatting
    // Note: We explicitly set all fields to avoid undefined values that might cause issues in production
    // Ensure numeric fields are properly converted (HTML select elements return strings)
    const priorityIdNum = typeof data.priorityId === 'string' ? parseInt(data.priorityId, 10) : data.priorityId;
    const statusIdNum = typeof data.statusId === 'string' ? parseInt(data.statusId, 10) : data.statusId;

    // Validate required fields before building payload
    if (!priorityIdNum || priorityIdNum <= 0 || isNaN(priorityIdNum)) {
      toast.error('Please select a valid priority in Step 3');
      setIsCreating(false);
      return;
    }
    if (!statusIdNum || statusIdNum <= 0 || isNaN(statusIdNum)) {
      toast.error('Please select a valid status in Step 3');
      setIsCreating(false);
      return;
    }

    const workOrderData: any = {
      clientId: data.selectedClient.id,
      clientLocationId: data.selectedClientLocationId, // Required field
      vendors: (data.selectedVendors && data.selectedVendors.length > 0)
        ? data.selectedVendors.map(v => ({
            vendorId: v.vendorId,
            isPrimary: v.isPrimary || false,
            status: v.status || 'Assigned',
            notes: v.notes || null,
            acknowledgeComplianceWarning: v.acknowledgeComplianceWarning || false,
            complianceOverrideReason: v.complianceOverrideReason || null,
          }))
        : [],
      assignedUserId: data.assignedUserId?.trim() || null,
      priorityId: priorityIdNum,
      statusId: statusIdNum,
      type: data.type || WorkOrderType.Regular,
      dueDate: formattedDueDate,
      description: data.description || null
    };

    // Add optional fields only if they have valid values (not undefined, not empty string for strings)
    if (data.categoryId !== undefined && data.categoryId !== null) {
      workOrderData.categoryId = data.categoryId;
    }
    if (data.equipmentId !== undefined && data.equipmentId !== null) {
      workOrderData.equipmentId = data.equipmentId;
    }
    // Note: unitId is collected in frontend but not yet supported in backend CreateWorkOrderDto
    if (data.templateId !== undefined && data.templateId !== null) {
      workOrderData.templateId = data.templateId;
    }
    if (data.externalWorkOrderNumber && data.externalWorkOrderNumber.trim()) {
      workOrderData.externalWorkOrderNumber = data.externalWorkOrderNumber.trim();
    }
    if (data.nteAmount !== undefined && data.nteAmount !== null && !isNaN(data.nteAmount)) {
      workOrderData.nteAmount = data.nteAmount;
    }

    // Remove any undefined values to ensure clean JSON serialization
    Object.keys(workOrderData).forEach(key => {
      if (workOrderData[key] === undefined) {
        delete workOrderData[key];
      }
    });

    console.log('Work order data being sent:', JSON.stringify(workOrderData, null, 2));
    console.log('Vendors count:', workOrderData.vendors?.length || 0);
    console.log('Vendors are optional, empty array is valid');

    try {
      createWorkOrderMutation.mutate(workOrderData);
    } catch (error) {
      console.error('Unexpected error during mutation:', error);
      setIsCreating(false);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  const handlePrint = () => {
    // Get the work order preview content
    const previewContent = document.querySelector('.print-area') as HTMLElement;
    if (!previewContent) {
      alert('Could not find content to print');
      return;
    }

    // Clone the content to avoid modifying the original
    const contentClone = previewContent.cloneNode(true) as HTMLElement;
    
    // Remove elements that shouldn't be printed
    const elementsToRemove = contentClone.querySelectorAll('button, .no-print');
    elementsToRemove.forEach(el => el.remove());
    
    // Create print-friendly HTML
    const printHtml = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>Work Order Preview</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            line-height: 1.4;
            color: #333;
            background: white;
        }
        
        .bg-white {
            background: white;
        }
        
        .border {
            border: 1px solid #e5e7eb;
        }
        
        .rounded-md {
            border-radius: 0.375rem;
        }
        
        .p-4, .p-6 {
            padding: 1rem;
        }
        
        .p-6 {
            padding: 1.5rem;
        }
        
        .text-lg {
            font-size: 1.125rem;
        }
        
        .font-medium {
            font-weight: 500;
        }
        
        .font-semibold {
            font-weight: 600;
        }
        
        .text-gray-900 {
            color: #111827;
        }
        
        .text-gray-600 {
            color: #4b5563;
        }
        
        .text-gray-500 {
            color: #6b7280;
        }
        
        .text-sm {
            font-size: 0.875rem;
        }
        
        .grid {
            display: grid;
        }
        
        .grid-cols-1 {
            grid-template-columns: repeat(1, minmax(0, 1fr));
        }
        
        .grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        
        .lg\\:grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        
        .gap-4 {
            gap: 1rem;
        }
        
        .gap-6 {
            gap: 1.5rem;
        }
        
        .flex {
            display: flex;
        }
        
        .items-center {
            align-items: center;
        }
        
        .space-x-2 > * + * {
            margin-left: 0.5rem;
        }
        
        .space-x-3 > * + * {
            margin-left: 0.75rem;
        }
        
        .space-x-4 > * + * {
            margin-left: 1rem;
        }
        
        .space-y-1 > * + * {
            margin-top: 0.25rem;
        }
        
        .space-y-2 > * + * {
            margin-top: 0.5rem;
        }
        
        .space-y-3 > * + * {
            margin-top: 0.75rem;
        }
        
        .space-y-4 > * + * {
            margin-top: 1rem;
        }
        
        .mb-2 {
            margin-bottom: 0.5rem;
        }
        
        .mb-3 {
            margin-bottom: 0.75rem;
        }
        
        .mb-4 {
            margin-bottom: 1rem;
        }
        
        .mb-6 {
            margin-bottom: 1.5rem;
        }
        
        .mt-2 {
            margin-top: 0.5rem;
        }
        
        .mt-3 {
            margin-top: 0.75rem;
        }
        
        .mt-4 {
            margin-top: 1rem;
        }
        
        .mt-6 {
            margin-top: 1.5rem;
        }
        
        .border-b {
            border-bottom: 1px solid #e5e7eb;
        }
        
        .border-gray-200 {
            border-color: #e5e7eb;
        }
        
        .bg-gray-50 {
            background-color: #f9fafb;
        }
        
        .bg-gray-100 {
            background-color: #f3f4f6;
        }
        
        .rounded-lg {
            border-radius: 0.5rem;
        }
        
        .rounded-full {
            border-radius: 9999px;
        }
        
        .px-2 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
        }
        
        .py-1 {
            padding-top: 0.25rem;
            padding-bottom: 0.25rem;
        }
        
        .text-xs {
            font-size: 0.75rem;
        }
        
        .inline-flex {
            display: inline-flex;
        }
        
        .bg-accent-color-100 {
            background-color: var(--accent-color-100);
        }
        
        .text-accent-color-800 {
            color: var(--accent-color-800);
        }
        
        .bg-green-100 {
            background-color: #dcfce7;
        }
        
        .text-green-800 {
            color: #166534;
        }
        
        .bg-yellow-100 {
            background-color: #fef3c7;
        }
        
        .text-yellow-800 {
            color: #92400e;
        }
        
        .bg-red-100 {
            background-color: #fee2e2;
        }
        
        .text-red-800 {
            color: #991b1b;
        }
        
        .bg-gray-200 {
            background-color: #e5e7eb;
        }
        
        .text-gray-800 {
            color: #1f2937;
        }
        
        .prose {
            max-width: none;
        }
        
        .prose-sm {
            font-size: 0.875rem;
        }
        
        .max-w-none {
            max-width: none;
        }
        
        .max-h-70vh {
            max-height: 70vh;
        }
        
        .overflow-y-auto {
            overflow-y: auto;
        }
        
        .first-page-break {
            page-break-before: always;
            margin-top: 20px;
        }
        
        @media print {
            body {
                margin: 0;
                padding: 15px;
            }
            
            .p-4, .p-6 {
                padding: 0.75rem;
            }
            
            .first-page-break {
                page-break-before: always;
                margin-top: 0;
            }
            
            .max-h-70vh {
                max-height: none;
            }
            
            .overflow-y-auto {
                overflow-y: visible;
            }
        }
        
        /* Hide elements that shouldn't print */
        .no-print {
            display: none !important;
        }
        
        button {
            display: none !important;
        }
    </style>
</head>
<body>
    ${contentClone.innerHTML}
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    
    if (printWindow) {
      printWindow.document.write(printHtml);
      printWindow.document.close();
      
      // Add script to auto-trigger print dialog when page loads
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500); // Small delay to ensure content is loaded
      };
    }
  };

  const handleClose = () => {
    navigate('/workorders');
  };

  const handleCreateAnother = () => {
    // Reset wizard state and navigate back to step 1
    navigate('/workorders/create');
  };

  // Check if we have the required data from previous steps
  if (!data.selectedClient) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-medium">Missing required data from previous steps</div>
        <div className="text-gray-500 mt-2">Please go back and complete the client selection</div>
        <button
          type="button"
          onClick={onPrevious}
          className="mt-4 btn-secondary"
        >
          Back to Previous Step
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selected Template Summary (read-only) */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Template Summary</h3>
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {data.templateId ? (
            <p>Using selected template from Step 3. Preview below includes the Default First Page first, then the selected template.</p>
          ) : (
            <p>No template selected in Step 3. Only the Default First Page (if enabled) will show in the preview.</p>
          )}
        </div>
      </div>

      {/* Work Order Preview (Combined: First Page + Selected Template) */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-6 print-area">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Work Order Preview</h3>
          {createdWorkOrder && (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-5 w-5 mr-2" />
              <span className="font-medium">Created: {createdWorkOrder.workOrderNumberFormatted || createdWorkOrder.workOrderNumber}</span>
            </div>
          )}
        </div>

        {/* Combined HTML Preview */}
        <div className="border rounded-lg p-4 bg-white max-h-[70vh] overflow-y-auto">
          {combinedPreviewHtml ? (
            <div className="prose max-w-none text-black" dangerouslySetInnerHTML={{ __html: sanitizeHtml(combinedPreviewHtml) }} />
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">Select a template in Step 3 to see the combined preview. The company default first page will be shown first, followed by the selected template.</div>
          )}
        </div>

        {/* Client and Vendor Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 mt-6">
          {/* Client Information */}
          <div className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
            <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <User className="h-4 w-4 mr-2" />
              Client Information
            </h5>
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-3">
              <div className="space-y-1 text-sm">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.selectedClient?.name || '-'}</div>
                {(data.selectedClientLocationName || data.selectedClientLocationCity || data.selectedClientLocationState || data.selectedClientLocationZipCode) && (
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    {(data.selectedClientLocationName && data.selectedClientLocationName.replace(' - Primary Location','')) || ''}
                  </div>
                )}
                {(() => {
                  const parts = [
                    data.selectedClientLocationCity,
                    data.selectedClientLocationState,
                    data.selectedClientLocationZipCode
                  ].filter(Boolean);
                  return parts.length ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400">{parts.join(', ')}</div>
                  ) : null;
                })()}
                {data.selectedClient?.phone && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    📞 {data.selectedClient.phone}
                  </div>
                )}
                {data.selectedClient?.email && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    ✉️ {data.selectedClient.email}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vendors Information */}
          <div className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
            <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <User className="h-4 w-4 mr-2" />
              Selected Vendors ({(data.selectedVendors?.length || 0)})
            </h5>
            <div className="space-y-3">
              {(data.selectedVendors || []).map((selectedVendor, index) => (
                <div key={selectedVendor.vendorId} className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {vendorsResponse?.find((v: any) => v.id === selectedVendor.vendorId)?.name || `Vendor ${index + 1}`}
                    </span>
                    {selectedVendor.isPrimary && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent-color-100 dark:bg-accent-color-800 text-accent-color-800 dark:text-accent-color-200">
                        Primary
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-400">Status:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedVendor.status}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-400">Assigned Date:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">
                        {new Date().toLocaleDateString()}
                      </span>
                    </div>
                    {selectedVendor.notes && (
                      <div>
                        <span className="font-medium text-gray-600 dark:text-gray-400">Notes:</span>
                        <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedVendor.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Work Order Details */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
          <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
            <FileText className="h-4 w-4 mr-2" />
            Work Order Details
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">Assigned User:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">
                {assignedUserResponse ? `${assignedUserResponse.firstName} ${assignedUserResponse.lastName}` : (data.assignedUserId || 'Not assigned')}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">Due Date:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100 flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {data.dueDate ? new Date(data.dueDate).toLocaleDateString() : 'Not set'}
              </span>
            </div>
            {data.externalWorkOrderNumber && (
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-400">External WO#:</span>
                <span className="ml-2 text-gray-900 dark:text-gray-100">{data.externalWorkOrderNumber}</span>
              </div>
            )}
            
          </div>
          
          {/* Description */}
          <div className="mt-4">
            <span className="font-medium text-gray-600 dark:text-gray-400">Description:</span>
            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
              <div 
                className="text-sm text-gray-900 dark:text-gray-100 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.description || 'No description provided') }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onPrevious}
          className="btn-secondary"
        >
          Back to Step 3
        </button>
        
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={handlePrint}
            className="btn-secondary flex items-center"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </button>
          
          {!createdWorkOrder ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCreateWorkOrder();
              }}
              disabled={isCreating || isSubmitting}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              {isCreating ? 'Creating...' : 'Save & Create Work Order'}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCreateAnother}
                className="btn-primary flex items-center"
              >
                <FileText className="h-4 w-4 mr-2" />
                Create Another Work Order
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                Close
              </button>
            </>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print-area {
            box-shadow: none !important;
            border: 1px solid #000 !important;
          }
          
          .btn-secondary,
          .btn-primary {
            display: none !important;
          }
          
          .border-t {
            border-top: 1px solid #000 !important;
          }
          
          .border-b {
            border-bottom: 1px solid #000 !important;
          }
          
          .border {
            border: 1px solid #000 !important;
          }
          
          .bg-gray-50 {
            background-color: #f9fafb !important;
          }
          
          .text-gray-600 {
            color: #374151 !important;
          }
          
          .text-gray-900 {
            color: #111827 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StepCompletion;
